import { m } from 'motion/react';
import { type CSSProperties, useMemo } from 'react';

import { type Asset } from '@/typings/api-models';

import Avatar from './Avatar';
import s from './UserImage.module.scss';

export type UserImageProps = {
  url?: string | null | Pick<Asset, 'url'>;
  name?: string | null;
  size?: number;
  className?: string;
  hasBorder?: boolean;
  borderColor?: string;
  color?: string | null;
  isActive?: boolean;
};

export const userIsAnonymous = (name?: string | null): boolean =>
  name === 'Anonymous' || name === 'Contact' || name === 'You';

export const useUserImageColor = ({
  isAnonymous,
  color,
  url,
}: Pick<UserImageProps, 'url' | 'color'> & { isAnonymous?: boolean }): CSSProperties =>
  useMemo(
    () => ({
      backgroundColor:
        (isAnonymous && 'var(--color-gray-25)') ||
        color ||
        (typeof url === 'string' && url.startsWith('#') && url) ||
        (!url && 'var(--color-gray-25)') ||
        'transparent',
      color:
        (isAnonymous && 'var(--color-gray-75)') ||
        ((color || (typeof url === 'string' && url.startsWith('#'))) && 'var(--color-gray-0)') ||
        'var(--color-gray-75)',
    }),
    [color, isAnonymous, url],
  );

/**
 * @deprecated: Please use the `UserAvatar` container from `src/containers`
 */
const UserImage: React.FC<UserImageProps> = ({
  url,
  name,
  size = 32,
  className = '',
  hasBorder,
  borderColor,
  color,
  isActive,
}) => {
  const isAnonymous = userIsAnonymous(name);
  const colors = useUserImageColor({ color, isAnonymous, url });

  return (
    <div
      className={`${s['user-image__wrapper']} ${className}`}
      style={{
        ...colors,
        boxShadow: (hasBorder && `0px 0px 0px 3px ${borderColor || 'var(--color-gray-0)'}`) || undefined,
        height: `${size}px`,
        minWidth: `${size}px`,
        overflow: (((!url && !name) || isAnonymous) && 'hidden') || undefined,
        width: `${size}px`,
      }}
      data-translate='false'
    >
      <Avatar url={typeof url === 'string' ? url : url?.url} name={isAnonymous ? '' : name} size={size} />
      {isActive && !isAnonymous && (
        <m.div
          initial={{ scale: 0 }}
          animate={{ scale: 1, transition: { delay: 1, duration: 0.5, type: 'spring' } }}
          className={s.active}
        />
      )}
    </div>
  );
};

export default UserImage;
